import * as React from 'react';
import {
  useLocationLegacy as useLocation,
  LinkLegacy as Link,
} from '@leagueplatform/routing';
import { track } from '@leagueplatform/analytics';
import {
  genesisStyled,
  Flex,
  Box,
  HeadingTwo,
  SubtleButton,
  BodyOneSecondary,
  ThemeProps,
  HeadingThree,
  BodyTwoSecondary,
  Image,
  PrimaryButton,
  Overline,
} from '@leagueplatform/genesis-commons';
import {
  SkeletonBox,
  GenesisRouterLink,
} from '@leagueplatform/web-common-components';
import { useIntl } from '@leagueplatform/locales';
import { Icon, ICONS } from '@leagueplatform/ui-kit';
import { UnavailableDeviceConnectModal } from '@leagueplatform/health-programs';
import { handleStaticAsset } from '@leagueplatform/asset-config';
import { ErrorPanel } from '#components/error-panel/index.ts';

import { DASHBOARD_ASSET_MAP } from '#types/dashboard-assets.type.ts';
import { useGetHealthMetrics } from '#hooks/use-get-health-metrics.hook.ts';
import { InsightsCard } from '../insights-card/insights-card';

const InsightCardContainer = genesisStyled(Box)`
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    grid-template-rows: auto;
    grid-gap: ${({ theme }: ThemeProps) => `${theme?.space.one}px`};
    @media screen and (max-width: ${({ theme }: ThemeProps) =>
      `${theme?.breakpoints.tablet}`}) {
      grid-template-columns: 1fr;
    }
`;

function StartTrackingInsights() {
  const { formatMessage } = useIntl();
  const [displayUnavailableOnWebModal, setDisplayUnavailableOnWebModal] =
    React.useState(false);
  const { pathname: path } = useLocation();

  return (
    <>
      <Flex flexDirection="column" marginY="five">
        <HeadingTwo>{formatMessage({ id: 'YOUR_METRICS' })}</HeadingTwo>
        <Flex
          paddingInlineStart={{ laptop: 'twoAndHalf' }}
          paddingTop={{ tablet: 'twoAndHalf', phone: 'one' }}
          justifyContent="space-between"
          alignItems="center"
        >
          <Box display={{ tablet: 'initial', phone: 'none' }}>
            <Image
              src={
                handleStaticAsset(
                  DASHBOARD_ASSET_MAP.DASHBOARD_START_TRACKING_INSIGHTS,
                ) as string
              }
              alt=""
              width="100%"
              height="100%"
              maxWidth="405px"
            />
          </Box>
          <Flex
            flexDirection="column"
            justifyContent="space-between"
            width="100%"
            maxWidth="375px"
            marginInlineStart={{ tablet: 'one' }}
          >
            <Box>
              <HeadingThree marginBottom="half">
                {formatMessage({ id: 'START_TRACKING_HEALTH_HEADER' })}
              </HeadingThree>
              <BodyTwoSecondary
                color="onSurface.text.primary"
                marginBottom="one"
              >
                {formatMessage({ id: 'START_TRACKING_HEALTH_BODY' })}
              </BodyTwoSecondary>
            </Box>
            <Flex
              alignItems={{ tablet: 'center', phone: 'flex-start' }}
              flexDirection="column"
              maxWidth={300}
            >
              <PrimaryButton
                onClick={() => {
                  setDisplayUnavailableOnWebModal(true);
                }}
              >
                {formatMessage({ id: 'CONNECT_APPS_DEVICES' })}
              </PrimaryButton>
              <GenesisRouterLink to={`${path}/insights`} marginTop="one">
                <Flex alignItems="center">
                  <Box display="inline" marginInlineEnd="half">
                    {formatMessage({ id: 'BROWSE_SUPPORTED_DATA' })}
                  </Box>
                  <Icon size={12} icon={ICONS.FORWARD_CHEVRON} />
                </Flex>
              </GenesisRouterLink>
            </Flex>
          </Flex>
        </Flex>
      </Flex>

      {displayUnavailableOnWebModal ? (
        <UnavailableDeviceConnectModal
          onClose={() => setDisplayUnavailableOnWebModal(false)}
        />
      ) : null}
    </>
  );
}

export const YourInsights = () => {
  const { formatMessage } = useIntl();
  const { pathname: path } = useLocation();

  const {
    isLoading,
    isError,
    isRefetching,
    data: healthMetrics,
    refetch,
    error,
  } = useGetHealthMetrics({
    interval: 'instant',
    fields: { type: ['type', 'value', 'unit', 'trend', 'timestamp'] },
  });

  if (isLoading) {
    return <SkeletonBox height="450px" marginTop="twoAndHalf" />;
  }

  if (isError) {
    return (
      <ErrorPanel
        error={error}
        isRefetching={isRefetching}
        onRetry={() => refetch()}
      />
    );
  }

  if (!healthMetrics?.data?.length) {
    return <StartTrackingInsights />;
  }

  return (
    <Flex flexDirection="column" marginTop="twoAndHalf">
      <HeadingTwo marginBottom="oneAndHalf">
        {formatMessage({ id: 'YOUR_METRICS' })}
      </HeadingTwo>
      <Overline as="h3" marginBottom="threeQuarters">
        {formatMessage({ id: 'APPS_AND_DEVICES' })}
      </Overline>
      <BodyOneSecondary marginBottom="oneAndHalf">
        {formatMessage({ id: 'YOUR_METRICS_SUBTITLE' })}
      </BodyOneSecondary>
      <InsightCardContainer>
        {healthMetrics.data.map(({ type, unit, value, trend, timestamp }) => (
          <InsightsCard
            key={type}
            type={type}
            trend={trend}
            timestamp={timestamp}
            onClick={() => {
              track(
                'dashboard.button_clicked.about_me_metrics.navigate_to_metric_item',
                {
                  text_detail: type,
                  content_id: '',
                  contentful_system: '',
                  card_index: '',
                  stack_name: '',
                  metric_id: '',
                  metric_status: '',
                },
              );
            }}
            unit={unit}
            value={value}
          />
        ))}
      </InsightCardContainer>
      <SubtleButton
        alignSelf="center"
        marginTop="three"
        marginBottom="two"
        as={Link}
        to={`${path}/insights`}
        textDecoration="none"
        onClick={() => {
          track(
            'dashboard.button_clicked.about_me_metrics.navigate_to_metric_item',
            {
              text_detail: 'all insights',
              content_id: '',
              contentful_system: '',
              card_index: '',
              stack_name: '',
              metric_id: '',
              metric_status: '',
            },
          );
        }}
      >
        {formatMessage({ id: 'VIEW_ALL_METRICS' })}
      </SubtleButton>
    </Flex>
  );
};
